import {useEffect, useCallback, useState, useMemo, useRef, MutableRefObject} from 'react'
import {tabbable, FocusableElement} from 'tabbable'

type Node = HTMLDivElement | null;

export interface FocusTrapProps {
	includeContainer?: boolean;
	initialFocus?: 'container' | Node;
	returnFocus?: boolean;
	updateNodes?: boolean;
	isActive?: boolean;
	onClickClose?;
}

const useFocusTrap = (options?: FocusTrapProps): MutableRefObject<Node> => {
	const node = useRef<Node>(null)
	const {includeContainer, initialFocus, returnFocus, updateNodes, isActive, onClickClose} = useMemo<FocusTrapProps>(
		() => ({
			includeContainer: false,
			initialFocus: null,
			returnFocus: true,
			updateNodes: false,
			isActive: false,
			...options,
		}),
		[options],
	)
	const [tabbableNodes, setTabbableNodes] = useState<FocusableElement[]>([])
	const [previousFocusedNode, setPreviousFocusedNode] = useState<Node>(null)
	const wasOpen = useRef<boolean>(false)

	const setInitialFocus = useCallback(() => {
		if (initialFocus === 'container') {
			node.current?.focus()
		} else {
			initialFocus?.focus()
		}
	}, [initialFocus])

	const updateTabbableNodes = useCallback(() => {
		const {current} = node

		if (current) {
			const getTabbableNodes = tabbable(current, {includeContainer})
			setTabbableNodes(getTabbableNodes)
			return getTabbableNodes
		}

		return []
	}, [includeContainer])

	useEffect(() => {
		updateTabbableNodes()
		if (node.current) setInitialFocus()
	}, [setInitialFocus, updateTabbableNodes])

	useEffect(() => {
		if (isActive && !wasOpen.current) {
			setPreviousFocusedNode(document.activeElement as Node)
			wasOpen.current = true
		}
	}, [wasOpen, isActive])

	useEffect(() => {
		return () => {
			if (previousFocusedNode && returnFocus && wasOpen.current) {
				previousFocusedNode.focus()
				wasOpen.current = false
				setPreviousFocusedNode(null)
			}
		}
	}, [returnFocus, onClickClose, wasOpen, previousFocusedNode])

	const handleKeydown = useCallback(
		event => {
			const {key, keyCode, shiftKey} = event

			let getTabbableNodes = tabbableNodes
			if (updateNodes) getTabbableNodes = updateTabbableNodes()

			if ((key === 'Tab' || keyCode === 9) && getTabbableNodes.length) {
				const firstNode = getTabbableNodes[0]
				const lastNode = getTabbableNodes[getTabbableNodes.length - 1]
				const {activeElement} = document

				if (!getTabbableNodes.includes(activeElement as FocusableElement)) {
					event.preventDefault()
					shiftKey ? lastNode.focus() : firstNode.focus()
				}

				if (shiftKey && activeElement === firstNode) {
					event.preventDefault()
					lastNode.focus()
				}

				if (!shiftKey && activeElement === lastNode) {
					event.preventDefault()
					firstNode.focus()
				}
			}
		},
		[tabbableNodes, updateNodes, updateTabbableNodes],
	)

	useEffect(() => {
		window.addEventListener('keydown', handleKeydown)

		return () => {
			window.removeEventListener('keydown', handleKeydown)
		}
	}, [handleKeydown])

	return node
}

export default useFocusTrap
